export type SessionResponse =
  | { authenticated: false }
  | {
      authenticated: true;
      user: {
        name: string;
        email: string;
        orgName: string;
        orgSlug: string;
        authProvider: string;
      };
    };

export type DeploymentEnvironment = "development" | "test" | "preview" | "production";

export type AuthProviders = {
  google: boolean;
  devLogin: boolean;
  environment: DeploymentEnvironment;
};

export type Profile = {
  name: string;
  email: string;
  orgName: string;
  orgSlug: string;
  timezone: string;
  /** The roles held on this membership, in the active organization. */
  roles: string[];
};

/**
 * The grant every administrative route requires.
 *
 * Used only to decide what is worth offering. The refusal itself belongs to the
 * server, which answers 404 to anyone without the grant whether or not the
 * interface drew a link.
 */
export const ORG_ADMIN_ROLE = "org_admin";

export type TrainingEvidence = {
  completedAt?: string | null;
  assignmentKind?: "required" | "optional";
  current?: boolean;
  completionSource?: "direct_quiz" | "equivalent_credit" | null;
  sourceObligationId?: string | null;
  sourceCompletedAt?: string | null;
};

export type DueModule = TrainingEvidence & {
  moduleId?: string;
  obligationId: string;
  moduleKey: string;
  /** e.g. "DOC-3". People refer to these documents by number, not by title. */
  documentKeys: string;
  title: string;
  description: string | null;
  dueAt: string | null;
  state: string;
  documentCount: number;
  acknowledgedCount: number;
  passed: boolean;
};

export type TrainingList = {
  modules: DueModule[];
  history?: DueModule[];
  optionalModules?: DueModule[];
  summary: { total: number; complete: number; overdue: number };
};

export type ModuleDocument = {
  documentRevisionId: string;
  documentKey: string;
  title: string;
  revision: string;
  contentType: "markdown" | "pdf";
  html: string | null;
  /**
   * Where the PDF bytes are fetched from: the primary document for a PDF
   * rendition, or the immutable signed original published beside a Markdown
   * primary. `renditionKind` tells the two apart.
   */
  pdfUrl: string | null;
  renditionKind: "pdf" | "signed_original" | null;
  changeSummaryHtml: string | null;
  changeDiffHtml: string | null;
  semanticDiff: SemanticDiff | null;
  previousRevision: string | null;
  blobSha256: string;
  acknowledged: boolean;
};

export type ModuleDetail = TrainingEvidence & {
  readOnly?: boolean;
  sourceAttemptId?: string | null;
  sourceDocumentRevision?: string | null;
  signedFileShaMatch?: boolean | null;
  obligationId: string;
  title: string;
  description: string | null;
  state: string;
  dueAt: string | null;
  policy: { questionsPerAttempt: number; passMark: number };
  documents: ModuleDocument[];
  attempts: { score: number; passed: boolean; submittedAt: string }[];
};

export type Quiz = {
  attemptToken: string;
  startedAt: string;
  policy: { questionsPerAttempt: number; passMark: number };
  questions: {
    questionId: string;
    prompt: string;
    options: { optionId: string; text: string }[];
  }[];
};

export type QuizResult = { score: number; passed: boolean; correct: string[] };

export type QuizEditorModuleSummary = {
  id: string;
  key: string;
  title: string;
  questionsPerAttempt: number;
  questionCount: number;
};

export type QuizEditorModule = Omit<QuizEditorModuleSummary, "questionCount"> & {
  documents: { key: string; title: string; revisionId: string; revision: string }[];
  questions: {
    id: string;
    prompt: string;
    explanation: string | null;
    position: number;
    options: { id: string; text: string; correct: boolean; position: number }[];
  }[];
};

export type QuizQuestionInput = {
  prompt: string;
  explanation?: string | null;
  options: { text: string; correct: boolean }[];
};

export type ApiCredentialScope = "quiz:read" | "quiz:write";

export type ApiCredential = {
  id: string;
  name: string;
  scopes: ApiCredentialScope[];
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
};

export type ModuleStatus =
  | "complete"
  | "overdue"
  | "in_progress"
  | "not_started"
  | "deferred"
  | "exempt";

export type Compliance = {
  modules: { moduleId: string; moduleKey: string; title: string; documentKeys: string }[];
  people: {
    membershipId: string;
    name: string;
    email: string;
    state: string;
    roles: string[];
    coverageStatus: "assigned" | "awaiting_curriculum" | "coverage_gap";
    statuses: Record<string, ModuleStatus>;
    complete: number;
    required: number;
    overdue: number;
  }[];
  summary: {
    people: number;
    required: number;
    complete: number;
    overdue: number;
    exempt: number;
    deferred: number;
    awaitingCurriculum: number;
    coverageGaps: number;
    completionRate: number;
  };
};

export type Governance = {
  curricula: {
    id: string;
    key: string;
    label: string;
    participantCount: number;
    modules: {
      id: string;
      key: string;
      title: string;
      documents: { key: string; title: string; revision: string }[];
    }[];
  }[];
  people: { membershipId: string; name: string; email: string; curriculumKeys: string[] }[];
  modules: { id: string; key: string; title: string }[];
  obligations: {
    id: string;
    membershipId: string;
    moduleId: string;
    moduleTitle: string;
    dueAt: string;
    adjustmentId: string | null;
    adjustmentAction: "defer" | "correct" | "revoke" | null;
  }[];
  cycles: {
    id: string;
    curriculumKey: string;
    curriculumLabel: string;
    name: string;
    startsOn: string;
    dueOn: string;
    recurrenceMonths: number | null;
    participantCount: number;
  }[];
};

export type Overdue = {
  count: number;
  overdue: {
    membershipId: string;
    name: string;
    email: string;
    moduleTitle: string;
    dueAt: string;
    daysOverdue: number;
  }[];
};

export type DeliveryIssues = {
  count: number;
  issues: {
    eventId: string;
    membershipId: string;
    name: string;
    notificationKind: "learner_digest" | "admin_digest";
    eventType: "soft_bounce" | "hard_bounce";
    occurredAt: string;
    reason: string;
  }[];
};

export type RecordCurriculum = {
  key: string;
  label: string;
};

export type SemanticDiffSegment = {
  kind: "same" | "added" | "removed";
  text: string;
};

export type SemanticDiffRow = {
  id: string;
  kind: "same" | "changed" | "added" | "removed";
  section: string | null;
  from: SemanticDiffSegment[] | null;
  to: SemanticDiffSegment[] | null;
  inline: SemanticDiffSegment[];
};

export type SemanticDiff = {
  rows: SemanticDiffRow[];
  changeCount: number;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Only declare a JSON body when there is one. Fastify rejects an empty body
  // sent with `content-type: application/json`, which is exactly what a POST
  // with no payload would otherwise do.
  const headers = init?.body
    ? { "content-type": "application/json", ...(init.headers ?? {}) }
    : (init?.headers ?? {});

  const response = await fetch(path, { ...init, headers });

  if (!response.ok) {
    const detail = await response.json().catch(() => ({ error: "request_failed" }));
    throw new Error(typeof detail.error === "string" ? detail.error : "request_failed");
  }

  return response.json() as Promise<T>;
}

export const api = {
  session: () => request<SessionResponse>("/api/auth/session"),
  providers: () => request<AuthProviders>("/api/auth/providers"),
  devSignIn: (email: string) =>
    request<{ ok: true }>("/api/auth/dev", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  signOut: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  profile: () => request<Profile>("/api/me"),
  updateProfile: (timezone: string) =>
    request<{ timezone: string }>("/api/me", {
      method: "PATCH",
      body: JSON.stringify({ timezone }),
    }),
  training: () => request<TrainingList>("/api/training"),
  trainingLibrary: () =>
    request<{
      modules: {
        moduleId: string;
        moduleKey: string;
        title: string;
        documentKeys: string;
        revision: string;
        obligationId: string | null;
      }[];
    }>("/api/training-library"),
  selfAssign: (moduleId: string) =>
    request<{ obligationId: string }>(
      `/api/training-library/${encodeURIComponent(moduleId)}/assign`,
      { method: "POST" },
    ),
  unsubscribe: (obligationId: string) =>
    request<{ unsubscribed: true }>(
      `/api/training/${encodeURIComponent(obligationId)}/unsubscribe`,
      { method: "POST" },
    ),
  module: (obligationId: string) => request<ModuleDetail>(`/api/training/${obligationId}`),
  acknowledge: (obligationId: string, documentRevisionId: string) =>
    request<{ acknowledged: true }>(`/api/training/${obligationId}/acknowledge`, {
      method: "POST",
      body: JSON.stringify({ documentRevisionId }),
    }),
  startQuiz: (obligationId: string) =>
    request<Quiz>(`/api/training/${obligationId}/quiz`, { method: "POST" }),
  materials: () =>
    request<{
      releases: { id: string; commitSha: string; state: string; activatedAt: string | null }[];
      modules: {
        moduleId: string;
        moduleKey: string;
        title: string;
        releaseId: string;
        revisionId: string;
        documentKey: string;
        revision: string;
        sha256: string;
        source: string | null;
      }[];
    }>("/api/admin/materials"),
  activationPreview: (releaseId: string) =>
    request<{
      activeReleaseId: string | null;
      modules: {
        moduleId: string;
        moduleKey: string;
        title: string;
        documentChanged: boolean;
        revisionId: string | null;
        affectedTrainees: number;
        affected: {
          obligationId: string;
          membershipId: string;
          state: string;
          learnerName: string;
          learnerEmail: string;
        }[];
      }[];
    }>(`/api/admin/materials/${encodeURIComponent(releaseId)}/activation`),
  activateRelease: (
    releaseId: string,
    input: {
      expectedActiveReleaseId?: string | null;
      choices: { moduleId: string; retrainExisting: boolean; note: string }[];
    },
  ) =>
    request<{ activated: string }>(
      `/api/admin/materials/${encodeURIComponent(releaseId)}/activate`,
      { method: "POST", body: JSON.stringify(input) },
    ),
  personTraining: (membershipId: string) =>
    request<{ modules: DueModule[]; history: DueModule[]; optionalModules: DueModule[] }>(
      `/api/admin/people/${encodeURIComponent(membershipId)}/training`,
    ),
  compliance: () => request<Compliance>("/api/admin/compliance"),
  governance: () => request<Governance>("/api/admin/governance"),
  quizModules: () => request<QuizEditorModuleSummary[]>("/api/admin/quiz-modules"),
  quizModule: (moduleId: string) =>
    request<QuizEditorModule>(`/api/admin/quiz-modules/${encodeURIComponent(moduleId)}`),
  addQuizQuestion: (moduleId: string, question: QuizQuestionInput) =>
    request<{ id: string }>(`/api/admin/quiz-modules/${encodeURIComponent(moduleId)}/questions`, {
      method: "POST",
      body: JSON.stringify(question),
    }),
  replaceQuizQuestion: (moduleId: string, questionId: string, question: QuizQuestionInput) =>
    request<{ id: string; supersedesQuestionId: string }>(
      `/api/admin/quiz-modules/${encodeURIComponent(moduleId)}/questions/${encodeURIComponent(questionId)}`,
      { method: "PATCH", body: JSON.stringify(question) },
    ),
  retireQuizQuestion: (moduleId: string, questionId: string) =>
    request<{ id: string; retired: true }>(
      `/api/admin/quiz-modules/${encodeURIComponent(moduleId)}/questions/${encodeURIComponent(questionId)}`,
      { method: "DELETE" },
    ),
  reorderQuizQuestions: (moduleId: string, questionIds: string[]) =>
    request<{ questionIds: string[] }>(
      `/api/admin/quiz-modules/${encodeURIComponent(moduleId)}/questions/order`,
      { method: "PUT", body: JSON.stringify({ questionIds }) },
    ),
  apiCredentials: () => request<ApiCredential[]>("/api/admin/api-credentials"),
  createApiCredential: (name: string, scopes: ApiCredentialScope[]) =>
    request<{ credential: ApiCredential; token: string }>("/api/admin/api-credentials", {
      method: "POST",
      body: JSON.stringify({ name, scopes }),
    }),
  revokeApiCredential: (credentialId: string) =>
    request<{ credential: ApiCredential }>(
      `/api/admin/api-credentials/${encodeURIComponent(credentialId)}/revoke`,
      { method: "POST" },
    ),
  assignCurriculum: (membershipId: string, curriculumId: string, reason?: string) =>
    request("/api/admin/curriculum-assignments", {
      method: "POST",
      body: JSON.stringify({
        membershipId,
        curriculumId,
        ...(reason?.trim() ? { reason: reason.trim() } : {}),
      }),
    }),
  createTrainingCycle: (input: {
    curriculumId: string;
    name: string;
    startsOn: string;
    dueOn: string;
    recurrenceMonths: number | null;
  }) => request("/api/admin/training-cycles", { method: "POST", body: JSON.stringify(input) }),
  createDeferral: (input: {
    obligationId: string;
    deferredUntil: string;
    reasonCategory: "temporary_absence" | "administrative_correction" | "other";
    rationale: string;
    supersedesAdjustmentId?: string;
  }) => request("/api/admin/deferrals", { method: "POST", body: JSON.stringify(input) }),
  revokeDeferral: (adjustmentId: string, input: { obligationId: string; rationale: string }) =>
    request(`/api/admin/deferrals/${encodeURIComponent(adjustmentId)}/revoke`, {
      method: "POST",
      body: JSON.stringify({ ...input, reasonCategory: "administrative_correction" }),
    }),
  createExemption: (input: {
    membershipId: string;
    moduleId: string;
    type: "not_applicable" | "waiver" | "equivalent_training";
    rationale: string;
    effectiveFrom: string;
    effectiveTo: string | null;
  }) => request("/api/admin/exemptions", { method: "POST", body: JSON.stringify(input) }),
  overdue: () => request<Overdue>("/api/admin/overdue"),
  deliveryIssues: () => request<DeliveryIssues>("/api/admin/delivery-issues"),
  recordCurricula: (asOf: string) =>
    request<{ curricula: RecordCurriculum[] }>(
      `/api/admin/records?asOf=${encodeURIComponent(asOf)}`,
    ),
  recordDownloadUrl: (curriculumKey: string, asOf: string, format: "markdown" | "csv" | "pdf") =>
    `/api/admin/records/${encodeURIComponent(curriculumKey)}?asOf=${encodeURIComponent(asOf)}&format=${format}`,
  submitQuiz: (
    obligationId: string,
    attemptToken: string,
    answers: { questionId: string; optionId: string }[],
  ) =>
    request<QuizResult>(`/api/training/${obligationId}/quiz/submit`, {
      method: "POST",
      body: JSON.stringify({ attemptToken, answers }),
    }),

  /** Organizations this person may act in, and which one they are in now. */
  async organizations(): Promise<{
    current: string;
    organizations: { slug: string; name: string }[];
  }> {
    return request("/api/auth/organizations");
  },

  async switchOrganization(orgSlug: string): Promise<{ orgSlug: string; orgName: string }> {
    return request("/api/auth/organization", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ orgSlug }),
    });
  },
};
